// src/screens/englishquiz/EnglishQuizStatistics.jsx

import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetEnglishQuizByIdQuery,
  useGetEnglishQuizStatisticsQuery,
  useRecalculateEnglishQuizMutation,
} from "../../slices/englishQuizApiSlice.js";

const formatNumber = (value, decimals = 2) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return number.toFixed(decimals).replace(/\.00$/, "");
};

const formatPercentage = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0%";
  }

  return `${number.toFixed(1)}%`;
};

const getErrorMessage = (error) => {
  if (!error) return "";

  if (typeof error === "string") {
    return error;
  }

  return (
    error?.data?.message ||
    error?.error ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};

const StatCard = ({
  title,
  value,
  subtitle,
  icon,
  color = "blue",
}) => {
  const colorClasses = {
    blue: {
      wrapper: "border-blue-100 bg-blue-50",
      icon: "bg-blue-100 text-blue-600",
      value: "text-blue-900",
    },
    emerald: {
      wrapper: "border-emerald-100 bg-emerald-50",
      icon: "bg-emerald-100 text-emerald-600",
      value: "text-emerald-900",
    },
    red: {
      wrapper: "border-red-100 bg-red-50",
      icon: "bg-red-100 text-red-600",
      value: "text-red-900",
    },
    amber: {
      wrapper: "border-amber-100 bg-amber-50",
      icon: "bg-amber-100 text-amber-600",
      value: "text-amber-900",
    },
    violet: {
      wrapper: "border-violet-100 bg-violet-50",
      icon: "bg-violet-100 text-violet-600",
      value: "text-violet-900",
    },
  };

  const classes =
    colorClasses[color] || colorClasses.blue;

  return (
    <div
      className={`rounded-2xl border p-5 shadow-sm ${classes.wrapper}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500">
            {title}
          </p>

          <p
            className={`mt-2 text-2xl font-bold ${classes.value}`}
          >
            {value}
          </p>

          {subtitle && (
            <p className="mt-1 text-xs text-slate-500">
              {subtitle}
            </p>
          )}
        </div>

        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${classes.icon}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

const ProgressBar = ({
  label,
  value,
  color = "blue",
}) => {
  const percentage = Math.min(
    Math.max(Number(value) || 0, 0),
    100,
  );

  const colors = {
    blue: "bg-blue-600",
    emerald: "bg-emerald-500",
    red: "bg-red-500",
    amber: "bg-amber-500",
    violet: "bg-violet-500",
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-700">
          {label}
        </span>

        <span className="text-sm font-semibold text-slate-900">
          {formatPercentage(percentage)}
        </span>
      </div>

      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all ${
            colors[color] || colors.blue
          }`}
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
};

const EnglishQuizStatistics = () => {
  const { quizId, id } = useParams();
  const navigate = useNavigate();

  const resolvedQuizId = quizId || id;

  const {
    data: quizData,
    isLoading: isQuizLoading,
    isError: isQuizError,
    error: quizError,
  } = useGetEnglishQuizByIdQuery(resolvedQuizId, {
    skip: !resolvedQuizId,
  });

  const {
    data: statisticsData,
    isLoading: isStatisticsLoading,
    isFetching: isStatisticsFetching,
    isError: isStatisticsError,
    error: statisticsError,
    refetch,
  } = useGetEnglishQuizStatisticsQuery(
    resolvedQuizId,
    {
      skip: !resolvedQuizId,
    },
  );

  const [
    recalculateQuiz,
    {
      isLoading: isRecalculating,
      error: recalculateError,
    },
  ] = useRecalculateEnglishQuizMutation();

  const statistics =
    statisticsData?.statistics ||
    statisticsData?.data ||
    statisticsData ||
    {};

  const quiz =
    quizData?.quiz ||
    quizData?.data ||
    quizData ||
    {};

  const normalizedStats = useMemo(() => {
    const totalAttempts = Number(
      statistics.totalAttempts ??
        statistics.attemptsCount ??
        statistics.total ??
        0,
    );

    const completedAttempts = Number(
      statistics.completedAttempts ??
        statistics.completed ??
        statistics.submittedAttempts ??
        totalAttempts,
    );

    const passedAttempts = Number(
      statistics.passedAttempts ??
        statistics.passed ??
        statistics.passCount ??
        0,
    );

    const failedAttempts = Number(
      statistics.failedAttempts ??
        statistics.failed ??
        statistics.failCount ??
        0,
    );

    const averagePercentage = Number(
      statistics.averagePercentage ??
        statistics.averageScorePercentage ??
        statistics.averageScore ??
        statistics.avgPercentage ??
        0,
    );

    const highestPercentage = Number(
      statistics.highestPercentage ??
        statistics.highestScorePercentage ??
        statistics.highestScore ??
        0,
    );

    const lowestPercentage = Number(
      statistics.lowestPercentage ??
        statistics.lowestScorePercentage ??
        statistics.lowestScore ??
        0,
    );

    const totalMarks = Number(
      statistics.totalMarks ??
        quiz.totalMarks ??
        0,
    );

    const averageMarks = Number(
      statistics.averageMarks ??
        statistics.avgMarks ??
        0,
    );

    const averageDuration = Number(
      statistics.averageDurationSeconds ??
        statistics.averageDuration ??
        statistics.avgDurationSeconds ??
        0,
    );

    const passRate =
      statistics.passRate !== undefined
        ? Number(statistics.passRate)
        : completedAttempts > 0
          ? (passedAttempts /
              completedAttempts) *
            100
          : 0;

    const failRate =
      statistics.failRate !== undefined
        ? Number(statistics.failRate)
        : completedAttempts > 0
          ? (failedAttempts /
              completedAttempts) *
            100
          : 0;

    return {
      totalAttempts,
      completedAttempts,
      passedAttempts,
      failedAttempts,
      averagePercentage,
      highestPercentage,
      lowestPercentage,
      totalMarks,
      averageMarks,
      averageDuration,
      passRate,
      failRate,
    };
  }, [statistics, quiz]);

  const handleRecalculate = async () => {
    if (!resolvedQuizId) return;

    try {
      await recalculateQuiz(
        resolvedQuizId,
      ).unwrap();

      refetch();
    } catch {
      // Mutation error is displayed below.
    }
  };

  const formatDuration = (seconds) => {
    const value = Number(seconds);

    if (!Number.isFinite(value) || value <= 0) {
      return "—";
    }

    const minutes = Math.floor(value / 60);
    const remainingSeconds = Math.round(
      value % 60,
    );

    if (!minutes) {
      return `${remainingSeconds}s`;
    }

    return `${minutes}m ${remainingSeconds}s`;
  };

  const isLoading =
    isQuizLoading ||
    isStatisticsLoading;

  const errorMessage = getErrorMessage(
    statisticsError ||
      quizError ||
      recalculateError,
  );

  if (!resolvedQuizId) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-3xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">
            Quiz ID is missing
          </h2>

          <p className="mt-2 text-sm text-slate-500">
            A valid English quiz ID is required to
            view statistics.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (
    (isQuizError || isStatisticsError) &&
    !isLoading
  ) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="mx-auto max-w-3xl rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
            <svg
              className="h-6 w-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
              />
              <path
                strokeLinecap="round"
                d="M12 8v5"
              />
              <path
                strokeLinecap="round"
                d="M12 16h.01"
              />
            </svg>
          </div>

          <h2 className="mt-4 text-lg font-semibold text-slate-900">
            Unable to load statistics
          </h2>

          <p className="mt-2 text-sm text-slate-500">
            {errorMessage ||
              "The quiz statistics could not be loaded."}
          </p>

          <div className="mt-5 flex justify-center gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Try Again
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
        <div className="mx-auto max-w-7xl animate-pulse">
          <div className="h-8 w-64 rounded bg-slate-200" />
          <div className="mt-2 h-4 w-96 max-w-full rounded bg-slate-200" />

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map(
              (_, index) => (
                <div
                  key={index}
                  className="h-32 rounded-2xl bg-white shadow-sm"
                />
              ),
            )}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="h-80 rounded-2xl bg-white shadow-sm" />
            <div className="h-80 rounded-2xl bg-white shadow-sm" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="mt-1 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Go back"
              >
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 18l-6-6 6-6"
                  />
                </svg>
              </button>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-bold text-slate-900 sm:text-2xl">
                    {quiz?.name ||
                      "English Quiz Statistics"}
                  </h1>

                  {quiz?.status && (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-600">
                      {quiz.status}
                    </span>
                  )}
                </div>

                <p className="mt-1 text-sm text-slate-500">
                  Performance overview and attempt
                  statistics
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isStatisticsFetching}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg
                  className={`h-4 w-4 ${
                    isStatisticsFetching
                      ? "animate-spin"
                      : ""
                  }`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 4v5h5M20 20v-5h-5"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5.1 15a8 8 0 0 0 13.8 2M18.9 9A8 8 0 0 0 5.1 7"
                  />
                </svg>
                Refresh
              </button>

              <button
                type="button"
                onClick={handleRecalculate}
                disabled={isRecalculating}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isRecalculating ? (
                  <svg
                    className="h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4Z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h5"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M20 20v-5h-5"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 9a8 8 0 0 1 13.5-3M19 15a8 8 0 0 1-13.5 3"
                    />
                  </svg>
                )}

                {isRecalculating
                  ? "Recalculating..."
                  : "Recalculate"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {recalculateError && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-800">
              Unable to recalculate statistics
            </p>

            <p className="mt-1 text-sm text-red-700">
              {getErrorMessage(
                recalculateError,
              )}
            </p>
          </div>
        )}

        {/* Overview */}
        <section>
          <div className="mb-4">
            <h2 className="text-base font-semibold text-slate-900">
              Overview
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Summary of student performance for this
              English quiz.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Attempts"
              value={
                normalizedStats.totalAttempts
              }
              subtitle="All recorded attempts"
              color="blue"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
                  />
                  <circle
                    cx="9"
                    cy="7"
                    r="4"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
                  />
                </svg>
              }
            />

            <StatCard
              title="Completed"
              value={
                normalizedStats.completedAttempts
              }
              subtitle="Submitted attempts"
              color="violet"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 11l3 3L22 4"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                  />
                </svg>
              }
            />

            <StatCard
              title="Passed"
              value={
                normalizedStats.passedAttempts
              }
              subtitle={`${formatPercentage(
                normalizedStats.passRate,
              )} pass rate`}
              color="emerald"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m5 12 4 4L19 6"
                  />
                </svg>
              }
            />

            <StatCard
              title="Failed"
              value={
                normalizedStats.failedAttempts
              }
              subtitle={`${formatPercentage(
                normalizedStats.failRate,
              )} fail rate`}
              color="red"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 6l12 12M18 6 6 18"
                  />
                </svg>
              }
            />

            <StatCard
              title="Average Score"
              value={formatPercentage(
                normalizedStats.averagePercentage,
              )}
              subtitle="Across completed attempts"
              color="blue"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3v18M3 12h18"
                  />
                </svg>
              }
            />

            <StatCard
              title="Highest Score"
              value={formatPercentage(
                normalizedStats.highestPercentage,
              )}
              subtitle="Best recorded percentage"
              color="emerald"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m5 15 7-7 7 7"
                  />
                </svg>
              }
            />

            <StatCard
              title="Lowest Score"
              value={formatPercentage(
                normalizedStats.lowestPercentage,
              )}
              subtitle="Lowest recorded percentage"
              color="amber"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m5 9 7 7 7-7"
                  />
                </svg>
              }
            />

            <StatCard
              title="Average Time"
              value={formatDuration(
                normalizedStats.averageDuration,
              )}
              subtitle="Average completion time"
              color="violet"
              icon={
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 7v5l3 2"
                  />
                </svg>
              }
            />
          </div>
        </section>

        {/* Performance */}
        <section className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Pass / Fail Performance
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Distribution of completed attempts.
              </p>
            </div>

            <div className="mt-6 space-y-6">
              <ProgressBar
                label="Pass rate"
                value={
                  normalizedStats.passRate
                }
                color="emerald"
              />

              <ProgressBar
                label="Fail rate"
                value={
                  normalizedStats.failRate
                }
                color="red"
              />

              <div className="grid grid-cols-2 gap-4 border-t border-slate-100 pt-5">
                <div className="rounded-xl bg-emerald-50 p-4">
                  <p className="text-xs font-medium text-emerald-700">
                    Passed
                  </p>

                  <p className="mt-1 text-xl font-bold text-emerald-900">
                    {
                      normalizedStats.passedAttempts
                    }
                  </p>
                </div>

                <div className="rounded-xl bg-red-50 p-4">
                  <p className="text-xs font-medium text-red-700">
                    Failed
                  </p>

                  <p className="mt-1 text-xl font-bold text-red-900">
                    {
                      normalizedStats.failedAttempts
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div>
              <h2 className="text-base font-semibold text-slate-900">
                Score Performance
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Score range and average performance.
              </p>
            </div>

            <div className="mt-6 space-y-5">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-4">
                <div>
                  <p className="text-xs text-slate-500">
                    Average
                  </p>

                  <p className="mt-1 text-lg font-bold text-slate-900">
                    {formatPercentage(
                      normalizedStats.averagePercentage,
                    )}
                  </p>
                </div>

                <div className="h-10 w-px bg-slate-200" />

                <div className="text-right">
                  <p className="text-xs text-slate-500">
                    Highest
                  </p>

                  <p className="mt-1 text-lg font-bold text-emerald-600">
                    {formatPercentage(
                      normalizedStats.highestPercentage,
                    )}
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    Lowest:{" "}
                    {formatPercentage(
                      normalizedStats.lowestPercentage,
                    )}
                  </span>

                  <span>
                    Highest:{" "}
                    {formatPercentage(
                      normalizedStats.highestPercentage,
                    )}
                  </span>
                </div>

                <div className="relative h-3 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-emerald-500"
                    style={{
                      width: `${Math.min(
                        Math.max(
                          normalizedStats.highestPercentage,
                          0,
                        ),
                        100,
                      )}%`,
                    }}
                  />

                  <div
                    className="absolute top-1/2 h-5 w-1 -translate-y-1/2 rounded-full bg-blue-700 shadow"
                    style={{
                      left: `${Math.min(
                        Math.max(
                          normalizedStats.averagePercentage,
                          0,
                        ),
                        100,
                      )}%`,
                    }}
                  />
                </div>

                <p className="mt-2 text-center text-xs text-slate-500">
                  Average score indicator
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs text-slate-500">
                    Average Marks
                  </p>

                  <p className="mt-1 text-lg font-bold text-slate-900">
                    {formatNumber(
                      normalizedStats.averageMarks,
                    )}
                    {normalizedStats.totalMarks > 0 &&
                      ` / ${formatNumber(
                        normalizedStats.totalMarks,
                      )}`}
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs text-slate-500">
                    Quiz Total
                  </p>

                  <p className="mt-1 text-lg font-bold text-slate-900">
                    {formatNumber(
                      normalizedStats.totalMarks,
                    )}{" "}
                    marks
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quiz Information */}
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-5">
            <h2 className="text-base font-semibold text-slate-900">
              Quiz Information
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Configuration associated with this
              statistics report.
            </p>
          </div>

          <div className="grid gap-5 p-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Subject
              </p>

              <p className="mt-1 text-sm font-semibold capitalize text-slate-800">
                {quiz?.questionBankSubject ||
                  quiz?.subject ||
                  "English"}
              </p>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Duration
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-800">
                {quiz?.durationMinutes ??
                  "—"}{" "}
                {quiz?.durationMinutes
                  ? "minutes"
                  : ""}
              </p>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Passing Percentage
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-800">
                {quiz?.passingPercentage ??
                  "—"}
                {quiz?.passingPercentage !==
                undefined
                  ? "%"
                  : ""}
              </p>
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                Total Questions
              </p>

              <p className="mt-1 text-sm font-semibold text-slate-800">
                {quiz?.totalQuestions ??
                  quiz?.questions?.length ??
                  "—"}
              </p>
            </div>
          </div>
        </section>

        {/* Empty State */}
        {normalizedStats.totalAttempts ===
          0 && (
          <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 19V5M4 19h16"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m7 15 4-4 3 3 5-7"
                />
              </svg>
            </div>

            <h3 className="mt-4 text-base font-semibold text-slate-900">
              No attempt data yet
            </h3>

            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              Statistics will appear here once students
              start submitting attempts for this English
              quiz.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default EnglishQuizStatistics;